import { Box, Button } from "@mui/material";
import Alert from "components/alert";
import NotAuthorized from "components/notAuthorized";
import useApi from "hooks/useApi";
import useApiCall from "hooks/useApiCall";
import useBase from "hooks/useBase";
import usePermissions from "hooks/usePermissions";
import useQueries from "hooks/useQueries";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { viewAllSales } from "../api/sales";
import AddEdit from "./AddEdit";
import Header from "./Header";
import Summary from "./Summary";
import AdvanceFilter from "./filter/AdvanceFilter";
import MainScreenFilter from "./filter/MainScreenFilter";
import MainTable from "./table/MainTable";
import ViewSales from "./viewSales/ViewSales";
import { getQueryFilterMap } from "../utils/getQueryFilterMap";

const Sales = () => {
  const { queries, setQueries, openAddEdit, openView } = useQueries();
  const filteringQueries = useMemo(
    () => queries?.get("filter") || {},
    [queries]
  );
  const [persistedData, setPersistedData] = useState({});
  const { updateBase, base } = useBase();
  const [activeMenu, setActiveMenu] = useState("");
  const [openFilter, setOpenFilter] = useState(false);
  const [allSales, setAllSales] = useState([]);
  // Helper states
  const [openAlert, setOpenAlert] = useState(false);
  const [reload, setReload] = useState(false);

  // Initialize filters with URL query params
  const getInitialFilters = () => {
    if (!filteringQueries) return {};

    const queryFilter = getQueryFilterMap(filteringQueries);
    return queryFilter ? { ...queryFilter } : {};
  };

  const [filters, setFilters] = useState(getInitialFilters());
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
  });

  const isFiltering = () => {
    let filtering = false;
    for (const key in filters) {
      if (Array.isArray(filters[key])) {
        if (filters[key].length > 0) filtering = true;
      } else {
        if (filters[key]) filtering = true;
      }
    }
    return filtering;
  };

  const permissions = usePermissions("sales");
  const all_props = {
    ...base,
    persistedData,
    setPersistedData,
    setReload,
    filters,
    setFilters,
    isFiltering,
    pagination,
    setPagination,
    allSales,
    setAllSales,
    permissions,
    openFilter,
    setOpenFilter,
  };

  const handleDelete = async (id) => {
    setOpenAlert(id);
  };

  const { fetchData: fetchDelete } = useApi();

  const handleDeleteConfirm = async () => {
    const oldAllInvoiceBank = JSON.parse(JSON.stringify(allSales));
    const itemIdToDelete = openAlert;
    setOpenAlert(false);
    setAllSales(allSales?.filter((bank) => bank?.id !== itemIdToDelete));
    const endpoint = {
      method: "delete",
      url: `/api/admin/sales/delete/${itemIdToDelete}`,
    };
    const result = await fetchDelete(endpoint);
    if (!result.success) {
      setAllSales(oldAllInvoiceBank);
      toast.error(result.message);
    }
    if (allSales?.length === 1) {
      setReload((pre) => !pre);
    }
  };

  const { data, loading, fetchData: fetchListData } = useApiCall();

  useEffect(() => {
    updateBase([
      "bankInvoiceStatuses",
      "banks",
      "paymentMethods",
      "references",
      "customers",
    ]);
  }, [reload]);

  useEffect(() => {
    let finalFilters = { ...filters };
    let finalPagination = { ...pagination };
    finalFilters.search = search;
    fetchListData(
      viewAllSales,
      {
        body_data: finalFilters,
        params: `page=${finalPagination?.current_page}&limit=${finalPagination?.per_page}`,
      },
      false
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload, search, pagination?.current_page, pagination?.per_page]);

  useEffect(() => {
    if (data) {
      const { data: allData, ...rest } = data;
      setAllSales(allData);
      setPagination(rest);
    }
  }, [data]);

  // Sync filters with URL query params
  useEffect(() => {
    if (!filteringQueries) return;

    const newFilters = getQueryFilterMap(filteringQueries);
    if (newFilters) {
      setFilters({ ...newFilters });
      setReload((pre) => !pre);
    }
  }, [filteringQueries]);

  return (
    <>
      {openAlert && (
        <Alert
          open={openAlert}
          handleClose={setOpenAlert}
          title="Delete Sale Invoice"
          desc={
            <Box>
              Are you sure you want to delete? This will delete this Sale
              Invoice permanently. <strong>You can't undo this action</strong>
            </Box>
          }
          buttons={
            <>
              <Button onClick={() => setOpenAlert(false)}>No, keep it</Button>
              <Button variant="solid" onClick={handleDeleteConfirm}>
                Yes, delete it
              </Button>
            </>
          }
        />
      )}
      <Header
        {...{
          activeMenu,
          setActiveMenu,
          filters,
          search,
          setSearch,
          setPagination,
          pagination,
          permissions,
          setOpenFilter,
        }}
      />
      {openAddEdit && (
        <AddEdit
          {...{
            openDrawer: openAddEdit,
            setOpenDrawer: () => setQueries(""),
            ...all_props,
          }}
        />
      )}
      {openView && (
        <ViewSales
          openDrawer={openView}
          setOpenDrawer={() => setQueries("")}
          {...all_props}
        />
      )}
      {activeMenu === "summary" && <Summary {...{ filters, search }} />}
      {
        !activeMenu && (
          // !!(permissions["list(own)"] || permissions["list(global)"]) && (
          <MainScreenFilter {...all_props} />
        )
        // )
      }

      {openFilter && (
        <AdvanceFilter
          openDrawer={openFilter}
          setOpenDrawer={setOpenFilter}
          {...all_props}
        />
      )}
      {!activeMenu &&
        (permissions.list ? (
          <MainTable
            handleDelete={handleDelete}
            loading={loading}
            {...all_props}
          />
        ) : (
          <NotAuthorized />
        ))}
    </>
  );
};

export default Sales;
